import React, { useState, useEffect } from 'react';
import Lottie from 'lottie-react';
import { motion, AnimatePresence } from 'framer-motion';
import desktopAnimationData from './ui/Scene.json';
import mobileAnimationData from './ui/Scene_mobile.json';

interface LoadingAnimationProps {
  onComplete: () => void;
}

// 设备检测hook
const useDeviceDetection = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkDevice = () => {
      // 检测屏幕宽度和高度
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;
      // 检测用户代理字符串
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobileUA = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
      
      // 判断为移动设备的条件：
      // 1. 屏幕宽度小于480px (包括440px等小屏手机)
      // 2. 或者宽度小于768px且高度大于宽度(竖屏手机)
      // 3. 或者用户代理包含移动设备标识（但排除iPad）
      const isMobileDevice = 
        (screenWidth < 480) || 
        (screenWidth < 768 && screenHeight > screenWidth) || 
        (isMobileUA && !userAgent.includes('ipad'));
      
      setIsMobile(isMobileDevice);
      
      // 调试日志
      console.log('设备检测:', {
        screenWidth,
        screenHeight,
        isMobileUA,
        isMobileDevice,
        userAgent: userAgent.substring(0, 50) + '...'
      });
    };

    checkDevice();
    window.addEventListener('resize', checkDevice);
    
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  return isMobile;
};

const LoadingAnimation: React.FC<LoadingAnimationProps> = ({ onComplete }) => {
  const [animationComplete, setAnimationComplete] = useState(false);
  const isMobile = useDeviceDetection();
  
  // 根据设备类型选择动画数据
  const animationData = isMobile ? mobileAnimationData : desktopAnimationData;

  // 管理body class来确保不影响滚动
  useEffect(() => {
    // 加载动画显示时，不需要改变body的滚动行为
    // 因为LoadingAnimation是fixed定位，完全在正常文档流之外
    return () => {
      // 清理时也不需要特殊处理
    };
  }, []);

  useEffect(() => {
    // 设置动画播放完成的延时，确保用户能看到完整动画
    const timer = setTimeout(() => {
      setAnimationComplete(true);
      // 添加额外的延时让退出动画播放完成
      setTimeout(() => {
        onComplete();
      }, 500);
    }, 1800); // 1.8秒后开始退出动画，加快进入Hero时间

    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <AnimatePresence>
      {!animationComplete && (
        <motion.div
          className="loading-animation-container"
          initial={{ opacity: 1 }}
          exit={{ 
            opacity: 0,
            scale: 0.95
          }}
          transition={{ 
            duration: 0.5, 
            ease: [0.25, 0.46, 0.45, 0.94]
          }}
        >
          {/* 背景装饰 */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#2D2D2D] via-[#3a3a3a] to-[#2D2D2D]" />
          
          {/* 动画容器 - 根据设备类型自动选择 */}
          <motion.div 
            className="absolute inset-0 z-10 flex items-center justify-center"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="w-full h-full relative">
              <Lottie
                animationData={animationData}
                loop={false}
                autoplay={true}
                style={{
                  width: '100%',
                  height: '100%',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: isMobile 
                    ? (window.innerWidth <= 450 
                        ? 'translate(-50%, -50%) scale(1.2)' // 440px手机适中缩放
                        : 'translate(-50%, -50%) scale(1.1)')
                    : 'translate(-50%, -50%) scale(1.0)', // 桌面端不缩放，保持原始大小
                  transformOrigin: 'center center',
                  objectFit: 'contain' // 确保动画完整显示不被裁剪
                }}
              />
            </div>
          </motion.div>

          {/* 加载提示文字 - 针对440px等小屏手机优化 */}
          <motion.div
            className={`absolute left-0 right-0 z-20 flex flex-col items-center justify-center px-3 ${
              isMobile 
                ? (window.innerWidth <= 450 ? 'bottom-12' : 'bottom-16') // 440px手机更靠下
                : 'bottom-24 md:bottom-32'
            }`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.0 }}
          >
            <div 
              className={`text-[#F1EFE0] text-center ${
                isMobile 
                  ? (window.innerWidth <= 450 ? 'text-xl max-w-xs' : 'text-2xl max-w-sm') // 440px手机用更小字号
                  : 'text-3xl md:text-4xl lg:text-5xl xl:text-6xl max-w-4xl'
              }`}
              style={{ fontFamily: 'Vixa-Regular, sans-serif' }}
            >
              Welcome to my world...
            </div>
            {/* 加载点动画 - 针对小屏手机调整 */}
            <div className={`flex justify-center items-center mt-3 ${
              isMobile 
                ? (window.innerWidth <= 450 ? 'space-x-1' : 'space-x-1.5') // 440px手机更紧凑间距
                : 'space-x-2 md:space-x-2.5'
            }`}>
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={i}
                  className={`bg-[#F1EFE0] rounded-full ${
                    isMobile 
                      ? (window.innerWidth <= 450 ? 'w-1 h-1' : 'w-1.5 h-1.5') // 440px手机用更小加载点
                      : 'w-2 h-2 md:w-2.5 md:h-2.5'
                  }`}
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.7, 1, 0.7],
                  }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    delay: i * 0.2,
                  }}
                />
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LoadingAnimation;
